import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button as MuiButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, ROUTES } from '../constants';
import { useAppDispatch, useAppSelector } from '../hooks';
import { createOrder } from '../features/ordersSlice';
import { clearMyCart, clearGuestCart } from '../features/cartSlice';
import { apiClient } from '../apiClient';
import { fetchCurrentUser, otpLogin } from '../features/authSlice';
import { formatCurrency, getEffectivePrice } from '../utils/pricing';
import { resolveImageUrl } from '../utils/image';
import { extractApiErrorMessage } from '../utils/errorHandler';
import { getStoredAttribution } from '../utils/attribution';
import { CouponQuote, StoreSettings } from '../types';
import TrustBadges from '../components/common/TrustBadges';

// Hide online payment until PhonePe production credentials are approved
// (build with REACT_APP_ONLINE_PAYMENT=off to disable)
const ONLINE_PAYMENT_ENABLED = process.env.REACT_APP_ONLINE_PAYMENT !== 'off';

const CheckoutPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { token, user } = useAppSelector((state) => state.auth);
  const { cart, guestItems, loading: cartLoading } = useAppSelector((state) => state.cart);
  const { creating, error } = useAppSelector((state) => state.orders);

  const savedAddresses = user?.addresses || [];
  const lastAddress = savedAddresses[0];

  const lineItems = (token ? cart?.items || [] : guestItems).filter((item) => Boolean(item.product));
  const subtotal = useMemo(
    () => lineItems.reduce((sum, item) => sum + getEffectivePrice(item.product) * item.quantity, 0),
    [lineItems]
  );

  const [form, setForm] = useState({
    customerName: user?.name || '',
    customerEmail: user?.email || '',
    customerPhone: user?.phone || '',
    street: lastAddress?.street || '',
    city: lastAddress?.city || '',
    state: lastAddress?.state || '',
    zipCode: lastAddress?.zipCode || '',
    country: lastAddress?.country || 'India',
    paymentMethod: ONLINE_PAYMENT_ENABLED ? 'phonepe' : 'cod',
    notes: ''
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [store, setStore] = useState<StoreSettings | null>(null);
  const [pincodeCheck, setPincodeCheck] = useState<{
    pincode: string;
    serviceable: boolean;
    codAvailable: boolean;
  } | null>(null);

  const verifyPincode = async (pincode: string) => {
    if (!/^\d{6}$/.test(pincode)) {
      setPincodeCheck(null);
      return;
    }
    try {
      const response = await apiClient.get(`/shipping/serviceability/${pincode}`);
      setPincodeCheck({
        pincode,
        serviceable: Boolean(response.data.serviceable),
        codAvailable: Boolean(response.data.codAvailable)
      });
    } catch {
      setPincodeCheck(null); // verification unavailable — never block checkout
    }
  };
  const [couponInput, setCouponInput] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<CouponQuote | null>(null);

  // Inline OTP verification for guests (lazy accounts — no separate signup)
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpDevMode, setOtpDevMode] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [welcomeNotice, setWelcomeNotice] = useState<string | null>(null);
  // Items snapshot taken when the OTP flow starts, so login-time cart
  // syncing can never change what gets ordered
  const pendingItems = useRef<Array<{ product: string; quantity: number }> | null>(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [resendIn]);

  const discount = appliedCoupon ? Math.min(appliedCoupon.discountAmount, subtotal) : 0;
  // Delivery mirrors the server rule: charged unless the pre-discount
  // subtotal clears the free-shipping threshold
  const deliveryCharge =
    store && store.shippingCharge > 0 &&
    !(store.freeShippingThreshold > 0 && subtotal >= store.freeShippingThreshold)
      ? store.shippingCharge
      : 0;
  // Online-payment incentive (mirrors order.service: percent off the
  // post-coupon goods value, never off delivery)
  const onlinePct = store?.onlinePaymentDiscountPercent || 0;
  const paymentDiscount =
    form.paymentMethod === 'phonepe' && onlinePct > 0
      ? Math.round(Math.max(0, subtotal - discount) * onlinePct) / 100
      : 0;
  const payableTotal = Math.max(0, subtotal - discount - paymentDiscount) + deliveryCharge;
  const belowMinimum = Boolean(store && store.minOrderValue > 0 && subtotal < store.minOrderValue);
  const amountToFreeShipping =
    store && store.shippingCharge > 0 && store.freeShippingThreshold > 0 && subtotal < store.freeShippingThreshold
      ? store.freeShippingThreshold - subtotal
      : 0;

  useEffect(() => {
    apiClient
      .get(`${API_ENDPOINTS.SITE_SETTINGS}/store`)
      .then((response) => setStore(response.data.store as StoreSettings))
      .catch(() => {});
  }, []);

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setApplyingCoupon(true);
    setCouponError(null);
    try {
      // Guests have no server cart yet — quote against the visible subtotal
      const response = token
        ? await apiClient.post(`${API_ENDPOINTS.COUPONS}/validate`, { code: couponInput.trim() })
        : await apiClient.post(`${API_ENDPOINTS.COUPONS}/quote`, {
            code: couponInput.trim(),
            subtotal
          });
      setAppliedCoupon(response.data as CouponQuote);
    } catch (err) {
      setAppliedCoupon(null);
      setCouponError(extractApiErrorMessage(err, 'Could not apply this coupon'));
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError(null);
  };

  // During and just after inline OTP login the cart is mid-handoff
  // (guest cart → server cart), so don't mistake that for "empty"
  const cartInFlux = otpOpen || creating || redirecting || Boolean(welcomeNotice) || (Boolean(token) && cartLoading);

  if (lineItems.length === 0 && !cartInFlux) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Your cart is empty
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add products before checkout.
        </Typography>
        <MuiButton variant="contained" component={RouterLink} to={ROUTES.PRODUCTS}>
          Continue shopping
        </MuiButton>
      </Box>
    );
  }

  const onChange = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const checkoutPhone = () => form.customerPhone.replace(/\D/g, '').slice(-10);
  const buildItemsPayload = () =>
    lineItems.map((item) => ({ product: item.product._id, quantity: item.quantity }));

  const validateForm = (): boolean => {
    const requiredFields: Array<keyof typeof form> = [
      'customerName',
      'customerEmail',
      'customerPhone',
      'street',
      'city',
      'state',
      'zipCode',
      'country'
    ];
    const missingField = requiredFields.find((field) => !String(form[field]).trim());
    if (missingField) {
      setSubmitError('Please fill all required checkout fields.');
      return false;
    }
    if (!/^[6-9]\d{9}$/.test(checkoutPhone())) {
      setSubmitError('Please enter a valid 10-digit Indian mobile number.');
      return false;
    }
    if (pincodeCheck?.pincode === form.zipCode.trim()) {
      if (!pincodeCheck.serviceable) {
        setSubmitError('We cannot deliver to this pincode yet — please use a different address.');
        return false;
      }
      if (form.paymentMethod === 'cod' && !pincodeCheck.codAvailable) {
        setSubmitError('Cash on Delivery is not available at this pincode. Please pay online.');
        return false;
      }
    }
    return true;
  };

  /** Returns true when the order was placed (navigation is underway). */
  const submitOrder = async (items: Array<{ product: string; quantity: number }>): Promise<boolean> => {
    const resultAction = await dispatch(
      createOrder({
        customerName: form.customerName.trim(),
        customerEmail: form.customerEmail.trim(),
        customerPhone: form.customerPhone.trim(),
        shippingAddress: {
          street: form.street.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          zipCode: form.zipCode.trim(),
          country: form.country.trim()
        },
        items,
        paymentMethod: form.paymentMethod,
        couponCode: appliedCoupon?.code,
        notes: form.notes.trim() || undefined,
        attribution: getStoredAttribution()
      })
    );

    if (createOrder.fulfilled.match(resultAction)) {
      // Save address to user profile for future pre-fill
      apiClient
        .post('/auth/me/addresses', {
          street: form.street.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          zipCode: form.zipCode.trim(),
          country: form.country.trim()
        })
        .then(() => dispatch(fetchCurrentUser()))
        .catch(() => {});

      dispatch(clearGuestCart());
      await dispatch(clearMyCart());

      if (form.paymentMethod === 'phonepe') {
        try {
          setRedirecting(true);
          const response = await apiClient.post(`${API_ENDPOINTS.PAYMENTS}/phonepe/initiate`, {
            orderId: resultAction.payload._id
          });
          // Hand off to PhonePe's hosted checkout page
          window.location.assign(response.data.redirectUrl);
          return true;
        } catch {
          setRedirecting(false);
          // Order exists but payment could not start — confirmation page shows it as unpaid
          navigate(`${ROUTES.ORDER_CONFIRMATION}/${resultAction.payload._id}?payment=failed`);
          return true;
        }
      }

      navigate(`${ROUTES.ORDER_CONFIRMATION}/${resultAction.payload._id}`);
      return true;
    }
    setSubmitError(resultAction.payload || 'Failed to place order');
    return false;
  };

  const placeOrder = async () => {
    setSubmitError(null);
    if (!validateForm()) return;

    if (!token) {
      // Guest: verify their number first — the account comes free
      pendingItems.current = buildItemsPayload();
      setOtp('');
      setOtpError(null);
      setOtpSending(true);
      try {
        const response = await apiClient.post(`${API_ENDPOINTS.AUTH}/otp/request`, {
          phone: checkoutPhone()
        });
        setOtpDevMode(Boolean(response.data.devMode));
        setResendIn(30);
        setOtpOpen(true);
      } catch (err) {
        setSubmitError(extractApiErrorMessage(err, 'Could not send OTP. Please try again.'));
      } finally {
        setOtpSending(false);
      }
      return;
    }

    // The cart briefly empties while a fresh login syncs it to the server —
    // fall back to the snapshot taken when the OTP flow started
    const items = lineItems.length > 0 ? buildItemsPayload() : pendingItems.current || [];
    await submitOrder(items);
  };

  const retryCheckoutOtp = async (via: 'text' | 'voice') => {
    setOtpError(null);
    try {
      await apiClient.post(`${API_ENDPOINTS.AUTH}/otp/retry`, { phone: checkoutPhone(), via });
      setResendIn(30);
    } catch (err) {
      setOtpError(extractApiErrorMessage(err, 'Could not resend OTP. Please try again.'));
    }
  };

  const verifyOtpAndContinue = async () => {
    setOtpError(null);
    setOtpVerifying(true);
    try {
      const result = await dispatch(
        otpLogin({ phone: checkoutPhone(), otp, name: form.customerName.trim() })
      );
      if (otpLogin.rejected.match(result)) {
        setOtpError(result.payload || 'Incorrect OTP. Please try again.');
        return;
      }

      const coupon = result.payload.welcomeCoupon;
      if (coupon && !appliedCoupon) {
        // First order gets the welcome discount — apply it and let the
        // customer see the new total before confirming
        try {
          const quote = await apiClient.post(`${API_ENDPOINTS.COUPONS}/quote`, {
            code: coupon.code,
            subtotal
          });
          setAppliedCoupon(quote.data as CouponQuote);
          setOtpOpen(false);
          setWelcomeNotice(
            `Welcome! Your personal code ${coupon.code} (${coupon.percent}% off) has been applied — check the new total and place your order.`
          );
          return;
        } catch {
          // Coupon quote hiccup — continue without it, code stays usable later
        }
      }

      // Existing account (or no welcome coupon): finish the order right away
      const placed = await submitOrder(pendingItems.current || buildItemsPayload());
      if (!placed) setOtpOpen(false);
    } finally {
      setOtpVerifying(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Checkout
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.2fr 0.8fr' },
          gap: 3,
          alignItems: 'start'
        }}
      >
        <Paper variant="outlined" sx={{ p: 3 }}>
          {(error || submitError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError || error}
            </Alert>
          )}
          {welcomeNotice && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setWelcomeNotice(null)}>
              {welcomeNotice}
            </Alert>
          )}

          <Typography variant="h6" sx={{ mb: 2 }}>
            Shipping details
          </Typography>

          {savedAddresses.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Saved addresses
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {savedAddresses.map((addr, i) => (
                  <Chip
                    key={i}
                    label={`${addr.street}, ${addr.city}`}
                    variant={
                      form.street === addr.street && form.city === addr.city && form.zipCode === addr.zipCode
                        ? 'filled'
                        : 'outlined'
                    }
                    color={
                      form.street === addr.street && form.city === addr.city && form.zipCode === addr.zipCode
                        ? 'primary'
                        : 'default'
                    }
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        street: addr.street,
                        city: addr.city,
                        state: addr.state,
                        zipCode: addr.zipCode,
                        country: addr.country
                      }))
                    }
                  />
                ))}
              </Box>
            </Box>
          )}

          <Stack spacing={1.5} sx={{ mb: 3 }}>
            <TextField label="Full name" value={form.customerName} onChange={onChange('customerName')} required />
            <TextField label="Email" type="email" value={form.customerEmail} onChange={onChange('customerEmail')} required />
            <TextField label="Phone" value={form.customerPhone} onChange={onChange('customerPhone')} required />
            <TextField label="Street address" value={form.street} onChange={onChange('street')} required />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField fullWidth label="City" value={form.city} onChange={onChange('city')} required />
              <TextField fullWidth label="State" value={form.state} onChange={onChange('state')} required />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                fullWidth
                label="ZIP code"
                value={form.zipCode}
                onChange={(e) => {
                  onChange('zipCode')(e as React.ChangeEvent<HTMLInputElement>);
                  verifyPincode(e.target.value.trim());
                }}
                required
                error={pincodeCheck?.pincode === form.zipCode.trim() && !pincodeCheck.serviceable}
                helperText={
                  pincodeCheck?.pincode === form.zipCode.trim() && !pincodeCheck.serviceable
                    ? 'Sorry, we cannot deliver to this pincode yet.'
                    : pincodeCheck?.pincode === form.zipCode.trim() && !pincodeCheck.codAvailable
                      ? 'Delivery available — Cash on Delivery is not offered at this pincode.'
                      : undefined
                }
              />
              <TextField fullWidth label="Country" value={form.country} onChange={onChange('country')} required />
            </Stack>
          </Stack>

          <Typography variant="h6" sx={{ mb: 2 }}>
            Payment
          </Typography>
          <TextField
            select
            fullWidth
            value={form.paymentMethod}
            onChange={onChange('paymentMethod')}
            sx={{ mb: 2 }}
          >
            {ONLINE_PAYMENT_ENABLED && (
              <MenuItem value="phonepe">
                Pay Online — UPI / Card / Net Banking · Recommended
                {onlinePct > 0 ? ` · ${onlinePct}% off` : ''}
              </MenuItem>
            )}
            {store?.codEnabled !== false && <MenuItem value="cod">Cash on Delivery</MenuItem>}
          </TextField>
          {ONLINE_PAYMENT_ENABLED && form.paymentMethod === 'cod' && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              {onlinePct > 0
                ? `Pay online and save ${onlinePct}% (${formatCurrency(Math.round(Math.max(0, subtotal - discount) * onlinePct) / 100)}) on this order — prepaid orders are also packed and dispatched first.`
                : 'Tip: prepaid orders are packed and dispatched first, and skip cash handling at your door.'}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Order notes (optional)"
            value={form.notes}
            onChange={onChange('notes')}
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Order summary
          </Typography>
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            {lineItems.map((item) => (
              <Box key={item.product._id} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Box
                  component="img"
                  src={resolveImageUrl(item.product.images?.[0] || '')}
                  alt={item.product.name}
                  sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1.5, flexShrink: 0 }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                    {item.product.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Qty: {item.quantity}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>
                  {formatCurrency(getEffectivePrice(item.product) * item.quantity)}
                </Typography>
              </Box>
            ))}
          </Stack>
          {appliedCoupon ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Chip
                color="success"
                variant="outlined"
                label={`${appliedCoupon.code} applied — you save ${formatCurrency(discount)}`}
                onDelete={removeCoupon}
              />
            </Box>
          ) : (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Coupon code"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applyCoupon();
                  }}
                />
                <MuiButton
                  variant="outlined"
                  onClick={applyCoupon}
                  disabled={applyingCoupon || !couponInput.trim()}
                >
                  {applyingCoupon ? 'Checking…' : 'Apply'}
                </MuiButton>
              </Box>
              {couponError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {couponError}
                </Typography>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Subtotal
            </Typography>
            <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
          </Box>
          {discount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="success.main">
                Discount ({appliedCoupon?.code})
              </Typography>
              <Typography variant="body2" color="success.main">
                -{formatCurrency(discount)}
              </Typography>
            </Box>
          )}
          {paymentDiscount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="success.main">
                Online payment discount ({onlinePct}%)
              </Typography>
              <Typography variant="body2" color="success.main">
                -{formatCurrency(paymentDiscount)}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: deliveryCharge > 0 || amountToFreeShipping > 0 ? 0.5 : 2 }}>
            <Typography variant="body2" color="text.secondary">
              Delivery
            </Typography>
            {deliveryCharge > 0 ? (
              <Typography variant="body2">{formatCurrency(deliveryCharge)}</Typography>
            ) : (
              <Typography variant="body2" color="success.main">
                Free
              </Typography>
            )}
          </Box>
          {amountToFreeShipping > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Add {formatCurrency(amountToFreeShipping)} more for free delivery
            </Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1">Total</Typography>
            <Typography variant="subtitle1">{formatCurrency(payableTotal)}</Typography>
          </Box>
          {belowMinimum && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Minimum order value is {formatCurrency(store?.minOrderValue || 0)} — add{' '}
              {formatCurrency((store?.minOrderValue || 0) - subtotal)} more to check out.
            </Alert>
          )}
          <MuiButton
            fullWidth
            variant="contained"
            onClick={placeOrder}
            disabled={
              creating ||
              redirecting ||
              otpSending ||
              belowMinimum ||
              (pincodeCheck?.pincode === form.zipCode.trim() && !pincodeCheck.serviceable)
            }
          >
            {redirecting
              ? 'Redirecting to PhonePe...'
              : creating
                ? 'Placing order...'
                : otpSending
                  ? 'Sending OTP...'
                  : form.paymentMethod === 'phonepe'
                    ? 'Place order & pay'
                    : 'Place order'}
          </MuiButton>
          {!token && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 1 }}
            >
              We&apos;ll confirm your mobile number with a quick OTP — no password needed.
            </Typography>
          )}
          <MuiButton fullWidth variant="text" sx={{ mt: 1 }} component={RouterLink} to={ROUTES.CART}>
            Back to cart
          </MuiButton>
          <Box sx={{ mt: 2 }}>
            <TrustBadges />
          </Box>
        </Paper>
      </Box>

      {/* Inline OTP verification for guest checkout */}
      <Dialog
        open={otpOpen}
        onClose={() => {
          if (!otpVerifying && !creating) setOtpOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Verify your number
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We sent an OTP to +91 {checkoutPhone()} to confirm your order.
          </Typography>
          {otpDevMode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Dev mode: use OTP <strong>123456</strong>
            </Alert>
          )}
          <TextField
            label="OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && otp.length >= 4 && !otpVerifying) verifyOtpAndContinue();
            }}
            fullWidth
            autoFocus
            inputProps={{
              inputMode: 'numeric',
              autoComplete: 'one-time-code',
              style: { letterSpacing: '0.4em', fontSize: 20 }
            }}
          />
          {otpError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {otpError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
            {resendIn > 0 ? (
              <Typography variant="caption" color="text.secondary">
                Resend in {resendIn}s
              </Typography>
            ) : (
              <>
                <MuiLink component="button" type="button" variant="caption" onClick={() => retryCheckoutOtp('text')}>
                  Resend OTP
                </MuiLink>
                <MuiLink component="button" type="button" variant="caption" onClick={() => retryCheckoutOtp('voice')}>
                  Get OTP via call
                </MuiLink>
              </>
            )}
            <MuiLink
              component="button"
              type="button"
              variant="caption"
              onClick={() => setOtpOpen(false)}
            >
              Change number
            </MuiLink>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <MuiButton
            fullWidth
            variant="contained"
            onClick={verifyOtpAndContinue}
            disabled={otp.length < 4 || otpVerifying || creating}
          >
            {otpVerifying || creating ? 'Verifying…' : 'Verify & place order'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CheckoutPage;
